import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import type { FrameworkRequirementScopeDTO } from '@deepguide-ai/types'
import {
  EVIDENCE_GUIDANCE_LEGAL_DISCLAIMER,
  evidenceGuidanceLegalDisclaimer,
} from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { StatCardSkeleton } from '@deepguide-ai/ui/stat-card-skeleton'
import { Textarea } from '@deepguide-ai/ui/textarea'
import {
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Download,
  MoreHorizontal,
} from 'lucide-react'
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs'
import { toast } from 'sonner'
import { useDocumentTitle } from 'usehooks-ts'
import { z } from 'zod/v3'
import {
  filterRequirementTree,
  hasActiveFilters,
} from '@/lib/framework-requirement-filter'
import { tallyTestStatusBuckets } from '@/lib/test-status-columns'
import {
  effectiveHideNa,
  encodeTestsListFilters,
  statCardStatusCounts,
  testsListQueryInput,
} from '@/lib/tests-list-filters'
import { trpc } from '@/lib/trpc'
import { useDebounce } from '@/hooks/useDebounce'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { AddTestDrawer } from '@/components/compliance/AddTestDrawer'
import { HideNaCheckbox } from '@/components/compliance/HideNaCheckbox'
import { ControlTree } from '@/components/compliance/ControlTree'
import {
  ControlsLensShell,
  ControlsLensTabs,
} from '@/components/compliance/ControlsLensShell'
import PageLoading from '@/components/PageLoading'
import { StatFilterCard } from '@/components/compliance/StatFilterCard'
import { TestsFilterBar } from '@/components/compliance/TestsFilterBar'
import { WorkspaceTestsExportButton } from '@/components/compliance/ExportControlsButton'
import {
  PageHeaderActions,
  PageHeaderExtra,
} from '@/components/layout/PageHeaderActions'

interface ControlFormData {
  controlId: string
  title: string
  description: string
}

type RequirementScopeTarget = Pick<
  FrameworkRequirementScopeDTO,
  | 'frameworkDefId'
  | 'frameworkControlId'
  | 'applicability'
  | 'source'
  | 'reason'
  | 'ruleKey'
>

/** Mirrors ControlTree requirements layout: requirement header + indented tests + section dividers. */
function ControlsRequirementsTreeSkeleton() {
  const sections = [
    { childCount: 2 },
    { childCount: 2 },
    { childCount: 1 },
  ] as const

  return (
    <div className='space-y-0.5' aria-hidden>
      {sections.map((section, sectionIndex) => {
        const isLastSection = sectionIndex === sections.length - 1
        return (
          <div
            key={sectionIndex}
            className={
              isLastSection ? undefined : 'border-border mb-1 border-b pb-1'
            }
          >
            <div className='flex items-start gap-2 rounded-sm px-2 py-1.5'>
              <Skeleton className='h-5 w-5 shrink-0 rounded-sm' />
              <Skeleton className='h-5 w-5 shrink-0 rounded-full' />
              <div className='min-w-0 flex-1 space-y-1'>
                <Skeleton className='h-4 w-[min(55%,280px)] max-w-full rounded-sm' />
                <Skeleton className='h-3 w-[min(80%,420px)] max-w-full rounded-sm' />
              </div>
              <div className='flex shrink-0 items-center gap-2'>
                <Skeleton className='h-3 w-14 rounded-sm' />
                <Skeleton className='h-1 w-12 rounded-full' />
              </div>
            </div>
            {Array.from({ length: section.childCount }, (_, childIndex) => (
              <div
                key={childIndex}
                className='flex items-center gap-2 rounded-sm py-1.5 pr-2 pl-9'
              >
                <span className='w-5 shrink-0' aria-hidden />
                <Skeleton className='h-5 w-5 shrink-0 rounded-full' />
                <Skeleton className='h-3.5 w-12 shrink-0 rounded-sm' />
                <Skeleton className='h-4 max-w-[220px] flex-1 rounded-sm' />
                <Skeleton className='h-5 w-14 shrink-0 rounded-full' />
                <Skeleton className='h-6 w-6 shrink-0 rounded-full' />
                <div className='flex shrink-0 items-center gap-2'>
                  <Skeleton className='h-3 w-14 rounded-sm' />
                  <Skeleton className='h-1 w-12 rounded-full' />
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

function ControlsExplorerPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { workspaceId, frameworkId } = Route.useParams()
  const isLeavingFrameworkPage = useRouterState({
    select: (state) =>
      state.status === 'pending' &&
      state.resolvedLocation?.pathname !== undefined &&
      state.location.pathname !== state.resolvedLocation.pathname,
  })
  const { canWorkspaceWrite, isAdmin, complianceRoleIds } =
    useWorkspacePermissions(workspaceId)
  const complianceRolesQuery = useQuery(trpc.complianceRole.list.queryOptions())
  const canManageRequirementScope =
    isAdmin ||
    (complianceRolesQuery.data ?? []).some(
      (role) =>
        role.roleKey === 'COMPLIANCE_MANAGER' &&
        complianceRoleIds.includes(role.id)
    )

  // Filters. URL-synced (nuqs) so a filtered requirement tree is bookmarkable
  // and survives the round-trip through a test's detail page — same param names
  // and semantics as /tests, since it is the same filter set.
  const [searchQuery, setSearchQuery] = useQueryState(
    'search',
    parseAsString.withDefault('')
  )
  const [selectedStatus, setSelectedStatus] = useQueryState(
    'status',
    parseAsString.withDefault('all')
  )
  useEffect(() => {
    if (['in_review', 'approved'].includes(selectedStatus)) {
      setSelectedStatus('submitted')
    }
  }, [selectedStatus, setSelectedStatus])
  const [selectedAssignee, setSelectedAssignee] = useQueryState(
    'assignee',
    parseAsString.withDefault('all')
  )
  // The Type filter is hidden on this page (parity with /tests), so the value
  // stays 'all' and the requirement filter's type branch is inert.
  const selectedTestType = 'all'
  // N/A rows hide by default; the URL stores only the unchecked deviation.
  const [hideNotApplicable, setHideNotApplicable] = useQueryState(
    'hideNa',
    parseAsBoolean.withDefault(true)
  )
  // Picking the "Not Applicable" status filter bypasses the hide — those rows
  // are the answer to that filter (shared rule: effectiveHideNa).
  const hideNaActive = effectiveHideNa({
    hideNa: hideNotApplicable,
    status: selectedStatus,
  })
  const [selectedLabelIds, setSelectedLabelIds] = useQueryState(
    'labels',
    parseAsArrayOf(parseAsString).withDefault([])
  )
  // RFC-224 depth / RFC-226 provenance — same params and semantics as /tests,
  // which is what lets both pages render the one shared filter bar.
  const [selectedInclusion, setSelectedInclusion] = useQueryState(
    'inclusion',
    parseAsStringLiteral(['all', 'core', 'scope', 'extended'] as const).withDefault(
      'all'
    )
  )
  // #1705 kind — same param and semantics as /tests (shared filter bar).
  const [selectedKind, setSelectedKind] = useQueryState(
    'kind',
    parseAsStringLiteral(
      ['all', 'policy', 'requirement', 'automated', 'manual'] as const
    ).withDefault('all')
  )
  const [auditorRequested, setAuditorRequested] = useQueryState(
    'auditorRequested',
    parseAsBoolean.withDefault(false)
  )
  const debouncedSearch = useDebounce(searchQuery, 200)

  // State for Create Test drawer. The "New test" entry point is hidden on this
  // page (test creation lives on /tests), so the drawer stays mounted but is
  // never opened — hence no setter for the control-def id.
  const [addTestDrawerOpen, setAddTestDrawerOpen] = useState(false)
  const [selectedControlDefId] = useState<string | null>(null)

  // State for highlighting newly imported/created test
  const [highlightedStatusId, setHighlightedStatusId] = useState<string | null>(
    null
  )

  // State for Control Management
  const [isCreateControlDialogOpen, setIsCreateControlDialogOpen] =
    useState(false)
  const [editingControl, setEditingControl] = useState<string | null>(null)
  const [controlFormData, setControlFormData] = useState<ControlFormData>({
    controlId: '',
    title: '',
    description: '',
  })

  // State for Edit Framework dialog
  const [isEditFrameworkDialogOpen, setIsEditFrameworkDialogOpen] =
    useState(false)
  const [editFrameworkName, setEditFrameworkName] = useState('')
  const [editFrameworkDescription, setEditFrameworkDescription] = useState('')

  // State for Delete Framework confirmation dialog
  const [isDeleteFrameworkDialogOpen, setIsDeleteFrameworkDialogOpen] =
    useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')

  const [requirementScopeDialog, setRequirementScopeDialog] = useState<{
    target: RequirementScopeTarget
    applicability: 'APPLICABLE' | 'NOT_APPLICABLE'
  } | null>(null)
  const [requirementScopeReason, setRequirementScopeReason] = useState('')

  // Fetch framework details
  const frameworkQuery = useQuery({
    ...trpc.guidedTemplate.getFramework.queryOptions({ id: frameworkId }),
    enabled: !!frameworkId,
  })

  // RFC-157 Phase C: enrolled frameworks for the in-page picker so users
  // can switch between multi-framework programs without URL navigation.
  const enrolledFrameworksQuery = useQuery(
    trpc.complianceProgram.listEnrolledFrameworks.queryOptions()
  )
  const enrolledFrameworks = enrolledFrameworksQuery.data?.frameworks ?? []
  const programId = enrolledFrameworksQuery.data?.programId ?? null

  const invalidateRequirementTree = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.listAllTests.queryKey(),
      }),
    ])

  const markRequirementNotApplicableMutation = useMutation({
    ...trpc.complianceProgram.markRequirementNotApplicable.mutationOptions(),
    onSuccess: async () => {
      await invalidateRequirementTree()
      toast.success('Requirement marked not applicable')
      setRequirementScopeDialog(null)
      setRequirementScopeReason('')
    },
    onError: (error) => toast.error(error.message),
  })

  const markRequirementApplicableMutation = useMutation({
    ...trpc.complianceProgram.markRequirementApplicable.mutationOptions(),
    onSuccess: async () => {
      await invalidateRequirementTree()
      toast.success('Requirement marked applicable')
      setRequirementScopeDialog(null)
      setRequirementScopeReason('')
    },
    onError: (error) => toast.error(error.message),
  })

  const resetRequirementScopeMutation = useMutation({
    ...trpc.complianceProgram.resetRequirementToAutomatic.mutationOptions(),
    onSuccess: async () => {
      await invalidateRequirementTree()
      toast.success('Requirement scope reset to automatic')
    },
    onError: (error) => toast.error(error.message),
  })

  const openRequirementScopeDialog = (
    target: RequirementScopeTarget,
    applicability: 'APPLICABLE' | 'NOT_APPLICABLE'
  ) => {
    setRequirementScopeReason('')
    setRequirementScopeDialog({ target, applicability })
  }

  const saveRequirementScope = () => {
    if (!programId || !requirementScopeDialog) {
      toast.error('Compliance program is not available')
      return
    }
    const reason = requirementScopeReason.trim()
    if (!reason) {
      toast.error('A reason is required')
      return
    }
    const input = {
      programId,
      frameworkDefId: requirementScopeDialog.target.frameworkDefId,
      frameworkControlId: requirementScopeDialog.target.frameworkControlId,
      reason,
    }
    if (requirementScopeDialog.applicability === 'NOT_APPLICABLE') {
      markRequirementNotApplicableMutation.mutate(input)
    } else {
      markRequirementApplicableMutation.mutate(input)
    }
  }

  const resetRequirementScope = (target: RequirementScopeTarget) => {
    if (!programId) {
      toast.error('Compliance program is not available')
      return
    }
    resetRequirementScopeMutation.mutate({
      programId,
      frameworkDefId: target.frameworkDefId,
      frameworkControlId: target.frameworkControlId,
    })
  }

  // Synchronous guard: isPending alone is too late (one frame of collapsed tree first).
  const [isFrameworkTreeLoading, setIsFrameworkTreeLoading] = useState(false)
  const prevFrameworkIdRef = useRef(frameworkId)

  const handleFrameworkChange = useCallback(
    (nextFrameworkId: string) => {
      if (nextFrameworkId === frameworkId) return
      setIsFrameworkTreeLoading(true)
      navigate({
        to: '/workspaces/$workspaceId/frameworks/$frameworkId',
        params: { workspaceId, frameworkId: nextFrameworkId },
      })
    },
    [frameworkId, navigate, workspaceId]
  )

  useLayoutEffect(() => {
    if (prevFrameworkIdRef.current === frameworkId) return
    prevFrameworkIdRef.current = frameworkId
    setIsFrameworkTreeLoading(true)
  }, [frameworkId])

  // RFC-214 §13: framework requirement → Test. The tree is the framework's
  // published requirement catalog with Tests bucketed by their own slot
  // citations — no ControlDef tier. Not paged: the spine is the whole catalog
  // (a requirement with zero tests is a gap we must show, §13.3 decision 4), so
  // there is no control page to walk.
  const treeQuery = useQuery({
    ...trpc.compliance.getControlsTree.queryOptions({
      frameworkId,
      labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
      groupBy: 'requirement',
    }),
    placeholderData: (previousData) => previousData,
  })

  // Complete, deduplicated candidates for local search and status-card counts.
  // Status selection stays out of this query so other buckets remain visible.
  const statSummaryQuery = useQuery(
    trpc.guidedTemplate.listAllTests.queryOptions({
      ...testsListQueryInput({
        search: debouncedSearch || undefined,
        assignee: selectedAssignee,
        labels: selectedLabelIds,
        framework: frameworkId,
        auditorRequested: auditorRequested || undefined,
      }),

    })
  )

  const showControlsTreeSkeleton = isFrameworkTreeLoading || treeQuery.isPending

  useEffect(() => {
    if (!isFrameworkTreeLoading) return
    if (treeQuery.isError) {
      setIsFrameworkTreeLoading(false)
      return
    }
    if (treeQuery.isSuccess && !treeQuery.isFetching) {
      setIsFrameworkTreeLoading(false)
    }
  }, [
    frameworkId,
    isFrameworkTreeLoading,
    treeQuery.isError,
    treeQuery.isFetching,
    treeQuery.isSuccess,
  ])

  const framework = frameworkQuery.data
  const tree = useMemo(() => treeQuery.data?.tree ?? [], [treeQuery.data?.tree])

  // Check if framework is built-in (no workspaceId means it's a system framework)
  const isBuiltInFramework =
    framework && !('workspaceId' in framework && framework.workspaceId)

  // Update document title with framework name
  useDocumentTitle(
    framework ? `${framework.name} Controls` : 'Controls Explorer'
  )

  // Flatten tree to show categories directly (skip framework level since we're already on framework page)
  // Requirement rows for this framework (framework node is implicit — we are
  // already on its page).
  const categories = useMemo(
    () => (tree.length > 0 ? tree[0].children || [] : []),
    [tree]
  )

  // Filter Tests inside each requirement by search / status / assignee. A
  // requirement keeps its row even when it ends up with zero matching Tests
  // while no filter is active — an uncovered requirement is the gap the page
  // exists to show (RFC-214 §13.3 decision 4). Under an active filter the
  // empty rows are noise, so they drop out.
  const searchLower = debouncedSearch.trim().toLowerCase()
  // Search and the narrowing filters want opposite things from a requirement
  // with no Tests — see framework-requirement-filter.ts for the rule.
  const filterState = useMemo(
    () => ({
      search: searchLower,
      status: selectedStatus,
      assignee: selectedAssignee,
      testType: selectedTestType,
      labelIds: selectedLabelIds,
      inclusion: selectedInclusion === 'all' ? undefined : selectedInclusion,
      kind: selectedKind === 'all' ? undefined : selectedKind,
      auditorRequested: auditorRequested || undefined,
    }),
    [
      auditorRequested,
      searchLower,
      selectedAssignee,
      selectedInclusion,
      selectedKind,
      selectedLabelIds,
      selectedStatus,
      selectedTestType,
    ]
  )
  const filtersActive = hasActiveFilters(filterState)

  const filteredTree = useMemo(
    () => filterRequirementTree(categories, filterState),
    [categories, filterState]
  )

  // ControlTree key to force remount when filters change (prevents stale nodes)
  const treeKey = useMemo(() => {
    const labelSignature = selectedLabelIds.join(',')
    return [
      searchLower,
      selectedStatus,
      selectedAssignee,
      selectedTestType,
      selectedInclusion,
      selectedKind,
      auditorRequested ? '1' : '0',
      hideNaActive ? '1' : '0',
      labelSignature,
    ].join('|')
  }, [
    auditorRequested,
    hideNaActive,
    selectedInclusion,
    selectedKind,
    selectedLabelIds,
    searchLower,
    selectedAssignee,
    selectedStatus,
    selectedTestType,
  ])

  // Count the complete DTO population locally; each shared Test appears once.
  const statusCounts = useMemo(
    () =>
      statCardStatusCounts(statSummaryQuery.data, {
        search: debouncedSearch,
        inclusion: filterState.inclusion,
        kind: filterState.kind,
      }),
    [statSummaryQuery.data, filterState.inclusion, filterState.kind, debouncedSearch]
  )
  const testStats = useMemo(
    () => tallyTestStatusBuckets(statusCounts),
    [statusCounts]
  )
  // The server only builds `summary` for a paginated call, so a depth filter —
  // which drops `limit` to tally the rows instead — has no summary to read.
  // Gate the cards on the counts we actually rendered, not on the summary.
  const hasTestStats = Object.values(statusCounts).some((count) => count > 0)
  const handleAddTestSuccess = async (statusId: string) => {
    // Refetch the controls tree to show the new test immediately
    await treeQuery.refetch()

    // Highlight the newly created test briefly
    setHighlightedStatusId(statusId)
    setTimeout(() => setHighlightedStatusId(null), 3000)
  }

  // Control CRUD mutations
  const createControlMutation = useMutation({
    ...trpc.guidedTemplate.createControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control created successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      })
      setIsCreateControlDialogOpen(false)
      setControlFormData({ controlId: '', title: '', description: '' })
    },
    onError: (error) => {
      toast.error('Failed to create control')
      console.error('Failed to create control:', error)
    },
  })

  const updateControlMutation = useMutation({
    ...trpc.guidedTemplate.updateControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control updated successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      })
      setEditingControl(null)
      setControlFormData({ controlId: '', title: '', description: '' })
    },
    onError: (error) => {
      toast.error('Failed to update control')
      console.error('Failed to update control:', error)
    },
  })

  const deleteControlMutation = useMutation({
    ...trpc.guidedTemplate.deleteControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control deleted successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      })
    },
    onError: (error) => {
      toast.error('Failed to delete control')
      console.error('Failed to delete control:', error)
    },
  })

  // Update framework mutation
  const updateFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.updateFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework updated successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getFramework.queryKey({
          id: frameworkId,
        }),
      })
      setIsEditFrameworkDialogOpen(false)
    },
    onError: (error) => {
      toast.error(`Failed to update framework: ${error.message}`)
    },
  })

  // Open edit framework dialog
  const handleOpenEditFramework = () => {
    setEditFrameworkName(framework?.name || '')
    setEditFrameworkDescription(framework?.description || '')
    setIsEditFrameworkDialogOpen(true)
  }

  // Save framework changes
  const handleSaveFramework = () => {
    if (!editFrameworkName.trim()) {
      toast.error('Framework name is required')
      return
    }
    updateFrameworkMutation.mutate({
      id: frameworkId,
      name: editFrameworkName.trim(),
      description: editFrameworkDescription.trim() || undefined,
    })
  }

  // Delete framework mutation
  const deleteFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.deleteFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework deleted successfully')
      navigate({
        to: '/workspaces/$workspaceId/compliance',
        params: { workspaceId },
      })
    },
    onError: (error) => {
      toast.error(`Failed to delete framework: ${error.message}`)
    },
  })

  // Export Control Matrix mutation (standard format for this framework)
  const exportMutation = useMutation({
    ...trpc.compliance.exportControlMatrix.mutationOptions(),
    onSuccess: (data) => {
      // Convert base64 to blob and trigger download
      const byteCharacters = atob(data.data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: data.contentType })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = data.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Control matrix exported successfully')
    },
    onError: (error) => {
      toast.error('Failed to export control matrix')
      console.error('Export failed:', error)
    },
  })

  const handleExport = () => {
    exportMutation.mutate({
      frameworkId,
    })
  }

  // Handle delete framework
  const handleDeleteFramework = () => {
    if (deleteConfirmationText !== framework?.name) {
      toast.error(
        'Please type the framework name correctly to confirm deletion'
      )
      return
    }
    deleteFrameworkMutation.mutate({ id: frameworkId })
  }

  // Control management handlers
  const handleCreateControl = () => {
    if (!controlFormData.controlId.trim()) {
      toast.error('Control ID is required')
      return
    }
    if (!controlFormData.title.trim()) {
      toast.error('Control title is required')
      return
    }
    if (!controlFormData.description.trim()) {
      toast.error('Control description is required')
      return
    }
    createControlMutation.mutate({
      frameworkId,
      controlId: controlFormData.controlId,
      title: controlFormData.title,
      description: controlFormData.description,
    })
  }

  const _handleEditControl = (control: {
    id: string
    controlId: string
    title: string
    description: string
  }) => {
    setEditingControl(control.id)
    setControlFormData({
      controlId: control.controlId,
      title: control.title,
      description: control.description,
    })
  }

  const handleUpdateControl = () => {
    if (!editingControl) return
    if (!controlFormData.title.trim()) {
      toast.error('Control title is required')
      return
    }
    if (!controlFormData.description.trim()) {
      toast.error('Control description is required')
      return
    }
    updateControlMutation.mutate({
      id: editingControl,
      title: controlFormData.title,
      description: controlFormData.description,
    })
  }

  const _handleDeleteControl = async (id: string, controlId: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete custom control "${controlId}"? This will also delete all associated evidence and tests for this workspace.`
      )
    ) {
      await deleteControlMutation.mutateAsync({ id })
    }
  }

  // The filter set the detail page inherits. `from: 'framework'` makes it
  // rebuild THIS page's requirement tree for its prev/next pager — it used to
  // say `from: 'tests'` and page the whole workspace's test list, so "next"
  // walked into tests this page never rendered.
  const listFilters = useMemo(
    () =>
      encodeTestsListFilters({
        search: debouncedSearch || undefined,
        status: selectedStatus,
        assignee: selectedAssignee,
        labels: selectedLabelIds,
        framework: frameworkId,
        hideNa: hideNotApplicable,
        // Depth, kind, and provenance narrow the tree too, so the pager has to
        // inherit them — otherwise "next" walks into rows this page never
        // rendered.
        inclusion: filterState.inclusion,
        kind: filterState.kind,
        auditorRequested: filterState.auditorRequested,
      }),
    [
      debouncedSearch,
      selectedStatus,
      selectedAssignee,
      selectedLabelIds,
      frameworkId,
      hideNotApplicable,
      filterState.inclusion,
      filterState.kind,
      filterState.auditorRequested,
    ]
  )

  const handleTestClick = (statusId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/tests/$testId',
      params: { workspaceId, testId: statusId },
      search: {
        from: 'framework',
        frameworkId,
        frameworkName: framework?.name,
        listFilters,
      },
    })
  }

  // Controls view mirrors the /tests coverage view: clicking a control row
  // opens its detail page (no inline test-creation on this page).

  return (
    <>
      <PageHeaderExtra>
        <div className='flex items-center gap-2'>
          <ChevronRight
            className='text-text-placeholder h-3.5 w-3.5 shrink-0'
            aria-hidden
          />
          {frameworkQuery.isLoading ? (
            <Skeleton className='h-8 w-[120px] rounded-sm' />
          ) : enrolledFrameworks.length > 1 ? (
            <Select value={frameworkId} onValueChange={handleFrameworkChange}>
              <SelectTrigger className='text-foreground h-8 w-auto max-w-[200px] gap-1 border-0 bg-transparent px-1 text-[15px] font-medium shadow-none'>
                <SelectValue>{framework?.name ?? 'Framework'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {enrolledFrameworks
                  .filter((f) => f.frameworkDefId != null)
                  .map((f) => (
                    <SelectItem key={f.id} value={f.frameworkDefId!}>
                      {f.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          ) : (
            <span className='font-heading text-foreground text-[15px] font-medium'>
              {framework?.name ?? 'Framework'}
            </span>
          )}
        </div>
      </PageHeaderExtra>
      <PageHeaderActions>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              className='h-8 w-8 rounded-sm p-0'
            >
              <MoreHorizontal className='h-4 w-4' />
              <span className='sr-only'>Framework actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-44'>
            <DropdownMenuItem
              onSelect={() => handleExport()}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Download className='h-4 w-4' />
              )}
              Export
            </DropdownMenuItem>
            {canWorkspaceWrite && (
              <>
                {framework?.canManage && (
                  <DropdownMenuItem onSelect={() => handleOpenEditFramework()}>
                    <Pencil className='h-4 w-4' />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/frameworks/$frameworkId/controls',
                      params: { workspaceId, frameworkId },
                    })
                  }
                >
                  <Plus className='h-4 w-4' />
                  Manage Controls
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeaderActions>
      {isLeavingFrameworkPage ? (
        <PageLoading className='h-[calc(var(--app-vh)_-_50px)] !w-full' />
      ) : (
        <ControlsLensShell>
          <div className='flex w-full flex-col px-6 pt-6 pb-12'>
          {/* Header section */}
          <div className='mb-2'>
            <h1 className='font-heading text-text-secondary text-[22px] leading-tight font-semibold'>
              {frameworkQuery.isLoading ? (
                <Skeleton className='h-7 w-48' />
              ) : (
                `${framework?.name ?? 'Framework'} requirements`
              )}
            </h1>
            <p className='text-text-subtle mt-1 text-sm leading-5'>
              Every published requirement in this framework, with the tests that
              evidence it. A requirement with no test is shown as a gap, not
              hidden.
            </p>
            {/* This page maps requirements to the evidence we ask for, which
                reads as a legal conclusion on a regulation unless it says
                otherwise. Shared constant — the same notice appears on every
                surface that recommends evidence. */}
            <p className='text-text-placeholder mt-2 text-xs leading-5'>
              {framework?.name
                ? evidenceGuidanceLegalDisclaimer(framework.name)
                : EVIDENCE_GUIDANCE_LEGAL_DISCLAIMER}
            </p>
          </div>
          <ControlsLensTabs />

          {/* Stat cards — Plan B: the Kanban status buckets, unified with the
            /tests page. */}
          {(showControlsTreeSkeleton ||
            statSummaryQuery.isPending ||
            hasTestStats) && (
            <div className='mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {showControlsTreeSkeleton || statSummaryQuery.isPending ? (
                Array.from({ length: 4 }, (_, index) => (
                  <StatCardSkeleton key={index} variant='controls' />
                ))
              ) : (
                <>
                  <StatFilterCard
                    label='Needs work'
                    value={testStats.needsWork}
                    valueClass='text-[#B56A11]'
                    active={selectedStatus === 'needs_work'}
                    onClick={() => setSelectedStatus(s => s === 'needs_work' ? 'all' : 'needs_work')}
                  />
                  <StatFilterCard
                    label='Submitted'
                    value={testStats.submitted}
                    valueClass='text-[#1D6B43]'
                    active={selectedStatus === 'submitted'}
                    onClick={() =>
                      setSelectedStatus((s) => (s === 'submitted' ? 'all' : 'submitted'))
                    }
                  />
                  <StatFilterCard
                    label='N/A'
                    value={testStats.notApplicable}
                    valueClass='text-muted-foreground'
                    active={selectedStatus === 'not_applicable'}
                    onClick={() =>
                      setSelectedStatus((s) =>
                        s === 'not_applicable' ? 'all' : 'not_applicable'
                      )
                    }
                  />
                </>
              )}
            </div>
          )}

          <div className='bg-background sticky top-0 z-10 pt-2 pb-4'>
            <TestsFilterBar
              value={{
                search: searchQuery,
                status: selectedStatus,
                assignee: selectedAssignee,
                labels: selectedLabelIds,
                inclusion: selectedInclusion,
                kind: selectedKind,
                source: auditorRequested ? 'auditor' : 'all',
              }}
              onChange={(patch) => {
                if (patch.search !== undefined) setSearchQuery(patch.search)
                if (patch.status !== undefined) setSelectedStatus(patch.status)
                if (patch.assignee !== undefined)
                  setSelectedAssignee(patch.assignee)
                if (patch.labels !== undefined) setSelectedLabelIds(patch.labels)
                if (patch.inclusion !== undefined) setSelectedInclusion(patch.inclusion)
                if (patch.kind !== undefined) setSelectedKind(patch.kind)
                if (patch.source !== undefined)
                  setAuditorRequested(patch.source === 'auditor')
              }}
              searchPlaceholder='Search requirements, tests, or assignees...'
              rightContent={
                <>
                  <HideNaCheckbox
                    hideNa={hideNotApplicable}
                    status={selectedStatus}
                    onCheckedChange={setHideNotApplicable}
                  />
                  {/* The same requirement-grouped workbook /tests and the
                      auditor portal export, already scoped to this framework.
                      Carries the filters the tree is applying, so the file
                      matches the rows on screen. */}
                  <WorkspaceTestsExportButton
                    frameworkId={frameworkId}
                    filters={testsListQueryInput({
                      search: debouncedSearch || undefined,
                      status: selectedStatus,
                      assignee: selectedAssignee,
                      labels: selectedLabelIds,
                      framework: frameworkId,
                      auditorRequested: auditorRequested || undefined,
                    })}
                    inclusion={selectedInclusion === 'all' ? undefined : selectedInclusion}
                    kind={selectedKind === 'all' ? undefined : selectedKind}
                    hideNa={hideNaActive}
                  />
                </>
              }
            />
          </div>
          {/* Requirement → Test tree — white panel (no outer border) */}
          <div className='overflow-hidden rounded-sm bg-card p-0.5'>
            {showControlsTreeSkeleton ? (
              <ControlsRequirementsTreeSkeleton />
            ) : (
              <ControlTree
                key={treeKey}
                nodes={filteredTree}
                onTestClick={handleTestClick}
                hideNotApplicable={hideNaActive}
                hasActiveFilters={filtersActive}
                highlightedStatusId={highlightedStatusId}
                initiallyExpanded={true}
                currentFrameworkSlug={framework?.slug}
                onMarkRequirementNotApplicable={
                  canManageRequirementScope
                    ? (target) =>
                        openRequirementScopeDialog(target, 'NOT_APPLICABLE')
                    : undefined
                }
                onMarkRequirementApplicable={
                  canManageRequirementScope
                    ? (target) =>
                        openRequirementScopeDialog(target, 'APPLICABLE')
                    : undefined
                }
                onResetRequirementAutomatic={
                  canManageRequirementScope ? resetRequirementScope : undefined
                }
                /* 136 requirement rows x their Tests, all expanded — mount only
                 the visible window. Resolves against ScreenataPageLayout's
                 ScrollArea viewport. */
                virtualize
              />
            )}
          </div>
          </div>
        </ControlsLensShell>
      )}
      {/* Create Test Drawer */}
      <AddTestDrawer
        open={addTestDrawerOpen}
        onOpenChange={setAddTestDrawerOpen}
        controlDefId={selectedControlDefId}
        frameworkId={frameworkId}
        onSuccess={handleAddTestSuccess}
      />
      <Dialog
        open={requirementScopeDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRequirementScopeDialog(null)
            setRequirementScopeReason('')
          }
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>
              {requirementScopeDialog?.applicability === 'NOT_APPLICABLE'
                ? 'Mark requirement not applicable'
                : 'Mark requirement applicable'}
            </DialogTitle>
            <DialogDescription>
              This decision applies only to{' '}
              {requirementScopeDialog?.target.frameworkControlId ??
                'this requirement'}
              . It does not change a test&apos;s execution status.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-2 px-6 py-4'>
            <Label htmlFor='requirement-scope-reason'>Reason *</Label>
            <Textarea
              id='requirement-scope-reason'
              aria-label='Reason'
              rows={4}
              value={requirementScopeReason}
              onChange={(event) =>
                setRequirementScopeReason(event.target.value)
              }
              placeholder='Explain why this requirement is or is not in scope'
            />
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setRequirementScopeDialog(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveRequirementScope}
              disabled={
                !requirementScopeReason.trim() ||
                markRequirementNotApplicableMutation.isPending ||
                markRequirementApplicableMutation.isPending
              }
            >
              Save decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import Test Drawer */}
      {/* Create/Edit Control Dialog */}
      <Dialog
        open={isCreateControlDialogOpen || editingControl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateControlDialogOpen(false)
            setEditingControl(null)
            setControlFormData({ controlId: '', title: '', description: '' })
          }
        }}
      >
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>
              {editingControl
                ? 'Edit Custom Control'
                : `Add ${isBuiltInFramework ? 'Custom ' : ''}Control`}
            </DialogTitle>
            <DialogDescription>
              {editingControl
                ? 'Update the custom control details. Control ID cannot be changed.'
                : isBuiltInFramework
                  ? 'Add a workspace-specific custom control to this built-in framework. This will only be visible in your workspace.'
                  : 'Add a new control to this custom framework.'}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='controlId'>Control ID *</Label>
              <Input
                id='controlId'
                placeholder='e.g., CC6.1, AC-01'
                value={controlFormData.controlId}
                onChange={(e) =>
                  setControlFormData({
                    ...controlFormData,
                    controlId: e.target.value,
                  })
                }
                disabled={!!editingControl}
              />
              <p className='text-muted-foreground mt-1 text-xs'>
                Unique identifier for this control
              </p>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='title'>Title *</Label>
              <Input
                id='title'
                placeholder='e.g., Logical Access Controls'
                value={controlFormData.title}
                onChange={(e) =>
                  setControlFormData({
                    ...controlFormData,
                    title: e.target.value,
                  })
                }
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description *</Label>
              <Textarea
                id='description'
                placeholder='Describe the control objective and requirements...'
                rows={5}
                value={controlFormData.description}
                onChange={(e) =>
                  setControlFormData({
                    ...controlFormData,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsCreateControlDialogOpen(false)
                setEditingControl(null)
                setControlFormData({
                  controlId: '',
                  title: '',
                  description: '',
                })
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={
                editingControl ? handleUpdateControl : handleCreateControl
              }
              disabled={
                createControlMutation.isPending ||
                updateControlMutation.isPending
              }
            >
              {editingControl
                ? updateControlMutation.isPending
                  ? 'Updating...'
                  : 'Update Control'
                : createControlMutation.isPending
                  ? 'Adding...'
                  : 'Add Control'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Framework Dialog */}
      <Dialog
        open={isEditFrameworkDialogOpen}
        onOpenChange={setIsEditFrameworkDialogOpen}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Edit Framework</DialogTitle>
            <DialogDescription>
              Update the framework name and description.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='edit-framework-name'>Name *</Label>
              <Input
                id='edit-framework-name'
                value={editFrameworkName}
                onChange={(e) => setEditFrameworkName(e.target.value)}
                placeholder='Framework name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='edit-framework-description'>Description</Label>
              <Textarea
                id='edit-framework-description'
                value={editFrameworkDescription}
                onChange={(e) => setEditFrameworkDescription(e.target.value)}
                placeholder='Framework description (optional)'
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className='flex-col gap-2 sm:flex-row sm:justify-between'>
            <Button
              variant='destructive'
              onClick={() => {
                setIsEditFrameworkDialogOpen(false)
                setDeleteConfirmationText('')
                setIsDeleteFrameworkDialogOpen(true)
              }}
            >
              <Trash2 className='h-4 w-4' />
              Delete Framework
            </Button>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={() => setIsEditFrameworkDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveFramework}
                disabled={updateFrameworkMutation.isPending}
              >
                {updateFrameworkMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Framework Confirmation Dialog */}
      <Dialog
        open={isDeleteFrameworkDialogOpen}
        onOpenChange={setIsDeleteFrameworkDialogOpen}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-destructive'>
              Delete Framework
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              framework and all associated controls and tests.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <p className='text-muted-foreground text-sm'>
              To confirm deletion, please type the framework name:{' '}
              <span className='text-foreground font-semibold'>
                {framework?.name}
              </span>
            </p>
            <Input
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder='Type framework name to confirm'
            />
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsDeleteFrameworkDialogOpen(false)
                setDeleteConfirmationText('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteFramework}
              disabled={
                deleteConfirmationText !== framework?.name ||
                deleteFrameworkMutation.isPending
              }
            >
              {deleteFrameworkMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Deleting...
                </>
              ) : (
                'Delete Framework'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Filter state, written to the URL by nuqs so a filtered requirement tree is
// bookmarkable — same param names as /tests, since it is the same filter set
// (see lib/tests-list-filters). `hideNa` is this page's own display toggle.
const frameworkDetailSearchSchema = z.object({
  // TanStack Router parses numeric-looking values (?search=2) as numbers.
  search: z.coerce.string().optional(),
  status: z.string().optional(),
  assignee: z.string().optional(),
  labels: z.string().optional(),
  depth: z.string().optional(),
  kind: z.string().optional(),
  // TanStack Router JSON-parses `?auditorRequested=true` / `?hideNa=true`
  // before search validation, so both arrive as booleans.
  auditorRequested: z.boolean().optional(),
  hideNa: z.boolean().optional(),
})

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/frameworks/$frameworkId/'
)({
  validateSearch: frameworkDetailSearchSchema,
  // Route-driven layout: the parent ScreenataPageLayout supplies the header
  // bar + scroll container (ScrollArea). Without this the page bypassed the
  // layout and rendered its own <Main> with no scroll. pad:false keeps the
  // page's own max-width content wrapper.
  // `titleHref`: the header reads "Frameworks › <name>", so the section title
  // is a breadcrumb node and should navigate like one.
  staticData: {
    pageHeader: { title: 'Frameworks', pad: false, titleHref: '/frameworks' },
  },
  component: ControlsExplorerPage,
})
